import { withPreviewStrings } from './preview-strings'
import { deDE } from './strings/de-DE'
import { deAT } from './strings/de-AT'
import { enGB } from './strings/en'
import { enGG } from './strings/en-GG'
import { plPL } from './strings/pl'
import { svSE } from './strings/sv'
import { fiFI } from './strings/fi'
import { daDK } from './strings/da'
import { nbNO } from './strings/nb'
import { roRO } from './strings/ro'
import { frFR } from './strings/fr'
import { frBE } from './strings/fr-BE'
import { nlNL } from './strings/nl'
import { nlBE } from './strings/nl-BE'
import { etEE } from './strings/et'
import { ltLT } from './strings/lt'
import { lvLV } from './strings/lv'
import { huHU } from './strings/hu'
import { csCZ } from './strings/cs'
import { enSE } from './strings/en-SE'
import { skSK } from './strings/sk'
import { ukUA } from './strings/uk'
import { ukVN } from './strings/uk-VN'
import { uzUZ } from './strings/uz'
import { ruUZ } from './strings/ru-UZ'
import { ruKZ } from './strings/ru-KZ'
import { hrHR } from './strings/hr'
import { plVN } from './strings/pl-VN'
import { lvVN } from './strings/lv-VN'
import { huVN } from './strings/hu-VN'

// Supported locales - keys are full locale codes
const SUPPORTED = {
  'de-DE': deDE,
  'de-AT': deAT,
  'en-GB': enGB,
  'en-GG': enGG,
  'pl-PL': plPL,
  'sv-SE': svSE,
  'fi-FI': fiFI,
  'da-DK': daDK,
  'nb-NO': nbNO,
  'ro-RO': roRO,
  'fr-FR': frFR,
  'fr-BE': frBE,
  'nl-NL': nlNL,
  'nl-BE': nlBE,
  'et-EE': etEE,
  'lt-LT': ltLT,
  'lv-LV': lvLV,
  'hu-HU': huHU,
  'cs-CZ': csCZ,
  'en-SE': enSE,
  'sk-SK': skSK,
  'uk-UA': ukUA,
  'uk-VN': ukVN,
  'uz-UZ': uzUZ,
  'ru-UZ': ruUZ,
  'ru-KZ': ruKZ,
  'hr-HR': hrHR,
  'pl-VN': plVN,
  'lv-VN': lvVN,
  'hu-VN': huVN,
}

// Map short language codes to full locale codes
const SHORT_TO_LOCALE = {
  de: 'de-DE',
  en: 'en-GB',
  pl: 'pl-PL',
  sv: 'sv-SE',
  fi: 'fi-FI',
  da: 'da-DK',
  nb: 'nb-NO',
  ro: 'ro-RO',
  fr: 'fr-FR',
  nl: 'nl-NL',
  et: 'et-EE',
  lt: 'lt-LT',
  lv: 'lv-LV',
  hu: 'hu-HU',
  cs: 'cs-CZ',
  sk: 'sk-SK',
  uk: 'uk-UA',
  uz: 'uz-UZ',
  kk: 'ru-KZ',
  hr: 'hr-HR',
}

const DEFAULT_LOCALE = 'en-GB'
const MARKET_LANGUAGE_MAP = {
  GermanyPurmo: 'de-DE',
  UKMyson: 'en-GB',
  UKPurmo: 'en-GG',
  PolandPurmo: 'pl-PL',
  PolandRadson: 'pl-PL',
  PolandVN: 'pl-VN',
  SwedenPurmo: 'sv-SE',
  SwedenThermotech: 'en-SE',
  AustriaVN: 'de-AT',
  FinlandPurmo: 'fi-FI',
  DenmarkPurmo: 'da-DK',
  NorwayPurmo: 'nb-NO',
  RomaniaPurmo: 'ro-RO',
  RomaniaVN: 'ro-RO',
  FranceFinimetal: 'fr-FR',
  FranceRadson: 'fr-FR',
  BelgiumRadson: 'fr-BE', // Default to French; can override via ?lang=nl-BE
  NetherlandsRadson: 'nl-NL',
  EstoniaPurmo: 'et-EE',
  LithuaniaPurmo: 'lt-LT',
  LatviaPurmo: 'lv-LV',
  LatviaVN: 'lv-VN',
  HungaryPurmo: 'hu-HU',
  HungaryVN: 'hu-VN',
  CzechiaVN: 'cs-CZ',
  SlovakiaPurmo: 'sk-SK',
  UkrainePurmo: 'uk-UA',
  UkraineVN: 'uk-VN',
  UzbekistanPurmo: 'ru-UZ',
  KazakhstanPurmo: 'ru-KZ',
  CroatiaPurmo: 'hr-HR',
  CroatiaVN: 'hr-HR',
}

// Normalize a language code to a full locale code
const normalizeLocale = (lang) => {
  if (!lang) return null
  const normalized = lang.replace('_', '-')
  // Already a full locale code
  if (SUPPORTED[normalized]) return normalized
  // Short code - expand to full locale
  if (SHORT_TO_LOCALE[normalized]) return SHORT_TO_LOCALE[normalized]
  return null
}

export const getLangFromUrl = () => {
  const params = new URLSearchParams(window.location.search)
  const candidate = params.get('lang')
  return normalizeLocale(candidate)
}

export const getLangFromMarket = (market) => {
  const mapped = MARKET_LANGUAGE_MAP[market]
  if (mapped && SUPPORTED[mapped]) return mapped
  return DEFAULT_LOCALE
}

export const resolveLang = (market) => {
  const langFromUrl = getLangFromUrl()
  if (langFromUrl) return langFromUrl
  return getLangFromMarket(market)
}

export const getStrings = (lang) => {
  const locale = normalizeLocale(lang) || DEFAULT_LOCALE
  // Portal preview texts, if any, sit on top of the built-in texts (see preview-strings.js)
  return withPreviewStrings(SUPPORTED[locale] || SUPPORTED[DEFAULT_LOCALE], locale)
}

// The built-in texts only: no preview texts on top, and reading them is not reported to the
// portal as a text the calculator shows (preview-manifest.js checks which keys have a text)
export const getBuiltInStrings = (lang) =>
  SUPPORTED[normalizeLocale(lang) || DEFAULT_LOCALE] || SUPPORTED[DEFAULT_LOCALE]

// The built-in texts of every locale, the same way
export const getEveryBuiltInStrings = () => Object.values(SUPPORTED)

export const resolveLocale = (lang, market) => {
  if (lang) {
    const normalized = normalizeLocale(lang)
    if (normalized) return normalized
  }
  return MARKET_LANGUAGE_MAP[market] || DEFAULT_LOCALE
}
